import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Grid, Type } from '../../app/StyleGuide';
import alertService from '../../services/AlertService';
import assessmentService from '../../services/assessmentService';
import { connectEventStream, shutdownEventStream } from '../event-stream';
import uuid from '../../services/uuid';
import { handleError } from '../../utils/apiUtils';
import { useUser } from '../authentication';
import { completeFeedbackStep } from './api/completeFeedbackStep';
import { getTasklistInstance } from './api/getTasklistByInstanceId';
import { startFeedbackStep } from './api/startFeedbackStep';
import AssessmentViewer from './assessments/AssessmentViewer';
import LoadingSection from './LoadingSection';
import { type Course, type FeedbackResponse, type Step } from './models/course';
import Section from './Section';
import { poll } from './utils/poll';

const Container = styled.div`
  display: flex;
  justify-content: center;
  h1 {
    font-size: ${Type.Scale._5};
    margin-bottom: ${Grid._7};
  }
`;

type TransformedAssessment = {
  name: string;
  instructions: string;
  overview: string;
  questions: any[];
  progress?: number;
  allAnswered?: boolean;
  responseVisibility: string;
  deleted: boolean;
  deletedBy: string | undefined;
  published: boolean;
  ownerId: string;
};

export default function FeedbackAssessmentViewer() {
  const { user } = useUser();
  const { courseId, stepId, email } = useParams<{ courseId: string; stepId: string; email: string }>();
  const [training, setTraining] = useState<Course>();

  const [currentStep, setCurrentStep] = useState<Step>();
  const [responseId, setResponseId] = useState<string>();

  const [assessment, setAssessment] = useState<TransformedAssessment>();
  const [showTaker, setShowTaker] = useState<boolean>(false);
  const [responseComplete, setResponseComplete] = useState<boolean>(false);
  const [feedbackStep, setFeedbackStep] = useState<FeedbackResponse>();

  // We don't get event responses for anonymous users, so this will determine
  // whether we should wait on events or poll for updates
  const isAnonymousTaker = !user?.userId;

  useEffect(() => {
    if (!email) {
      return;
    }

    connectEventStream(email);

    return () => {
      if (user?.userId) {
        connectEventStream(user.userId);
      } else {
        shutdownEventStream();
      }
    };
  }, [courseId, email, user?.userId]);

  useEffect(() => {
    getTasklistInstance(courseId)
      .then((trainingResponse: Course | null) => {
        if (!trainingResponse) return;
        setTraining(trainingResponse);

        let feedbackResponse = undefined;

        const feedbackTask = trainingResponse.tasks?.find(task => task.id.id === stepId);
        if (feedbackTask?.task && 'feedbackResponses' in feedbackTask.task) {
          feedbackResponse = feedbackTask.task.feedbackResponses.find(
            (response: { email: string }) => response.email === email
          );
        }

        const responseId = feedbackResponse?.responseId || uuid.generate();
        setResponseComplete(feedbackResponse?.status?.includes('complete') ?? false);
        setFeedbackStep(feedbackResponse);
        setResponseId(responseId);
        setCurrentStep(feedbackTask);

        setShowTaker(false);

        if (feedbackTask?.task && 'relatedTask' in feedbackTask.task && feedbackResponse?.status === 'not_started') {
          assessmentService
            .getAssessment(feedbackTask.task.relatedTask?.task.assessmentId, trainingResponse.assignedUser)
            .then((response: TransformedAssessment) => {
              setAssessment(response);
            })
            .catch(handleError);
        } else {
          getAssessmentResponse(responseId, trainingResponse.assignedUser);
        }
      })
      .catch(handleError);
  }, [courseId]);

  useEffect(() => {
    if (!responseId || !training?.assignedUser) return;
    getAssessmentResponse(responseId, training.assignedUser);
  }, [responseId, training?.assignedUser]);

  const getAssessmentResponse = (respId: string, assignedUser: string) => {
    return assessmentService
      .getAssessmentResponseWithQuestions(respId, assignedUser)
      .then((response: TransformedAssessment) => {
        setAssessment(response);
      })
      .catch((error: AxiosError) => {
        if (error.status !== 404) {
          handleError(error);
        }
      });
  };

  const startAssessment = () => {
    if (!responseId || !training || !currentStep) return;
    return startFeedbackStep(training.id, currentStep, training.assignedUser, email, responseId, email, true)
      .then(() => getAssessmentResponse(responseId, training.assignedUser).then(() => setShowTaker(true)))
      .catch(handleError);
  };

  const completeStep = () => {
    if (feedbackStep?.status !== 'complete' && training && currentStep && responseId) {
      completeFeedbackStep(training.id, currentStep, email, responseId, email, true)
        .then(() => setResponseComplete(true))
        .catch(handleError);
    }
  };

  const handleAllQuestionsAnswered = () => {
    poll(
      () => {
        return assessmentService
          .getAssessmentResponseWithQuestions(responseId, training?.assignedUser)
          .then((response: TransformedAssessment) => {
            if (!response?.allAnswered) {
              return Promise.reject(new Error('Assessment questions not all answered'));
            }

            setShowTaker(false);
            setFeedbackStep(prev => (prev ? { ...prev, status: 'complete' } : prev));
            setAssessment(response);
            return Promise.resolve(response);
          });
      },
      { interval: 1000, maxAttempts: 5, initialDelay: 1000 }
    ).catch(() => {
      alertService.showError();
    });
  };

  return (
    <Container>
      {!assessment ? (
        <LoadingSection />
      ) : !responseComplete ? (
        <AssessmentViewer
          assessment={assessment}
          progress={assessment.progress ?? 0}
          handleAllQuestionsAnswered={handleAllQuestionsAnswered}
          allAnswered={!!assessment.allAnswered}
          setShowTaker={setShowTaker}
          startAssessment={startAssessment}
          completeAssessment={completeStep}
          showTaker={showTaker}
          step={feedbackStep ?? ({} as object)}
          takerId={email}
          isPublicAssessment={true}
          bypassAnswerCheck={isAnonymousTaker}
        />
      ) : (
        <Section>
          <p>You have finished the {assessment?.name} assessment.</p>
          <p>You may close this window. Thank you for your time.</p>
        </Section>
      )}
    </Container>
  );
}
